"use client";

import {
  useId,
  useRef,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type ReactNode,
  type SelectHTMLAttributes,
} from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { AlertCircle, Check, Loader2, Search, X } from "lucide-react";

export function Button({
  variant = "primary",
  size = "md",
  loading,
  children,
  className = "",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md";
  loading?: boolean;
}) {
  return (
    <button
      type="button"
      {...props}
      disabled={props.disabled || loading}
      className={`button button--${variant} button--${size} ${className}`}
      aria-busy={loading || undefined}
    >
      {loading && <Loader2 size={16} className="spin" aria-hidden="true" />}
      {children}
    </button>
  );
}
export function IconButton({
  label,
  children,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & { label: string }) {
  return (
    <TooltipPrimitive.Provider delayDuration={350}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>
          <button
            type="button"
            {...props}
            aria-label={label}
            className={`icon-button ${props.className ?? ""}`}
          >
            {children}
          </button>
        </TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal>
          <TooltipPrimitive.Content className="tooltip" sideOffset={6}>
            {label}
            <TooltipPrimitive.Arrow />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
export function Field({
  label,
  error,
  className = "",
  ...props
}: InputHTMLAttributes<HTMLInputElement> & { label: string; error?: string }) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  return (
    <div className={`field ${className}`}>
      <label htmlFor={id}>
        {label}
        {props.required && <span className="required"> *</span>}
      </label>
      <input
        {...props}
        id={id}
        className="input"
        aria-invalid={!!error}
        aria-describedby={error ? `${id}-error` : undefined}
      />
      {error && (
        <span className="field-error" id={`${id}-error`} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
export function Select({
  label,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement> & { label: string }) {
  const id = useId();
  return (
    <div className="field">
      <label htmlFor={id}>{label}</label>
      <select {...props} id={id} className="input">
        {children}
      </select>
    </div>
  );
}
export function SearchField({
  label = "검색",
  showLabel = false,
  ...props
}: InputHTMLAttributes<HTMLInputElement> & {
  label?: string;
  showLabel?: boolean;
}) {
  const generatedId = useId();
  const id = props.id ?? generatedId;
  const control = (
    <div className="search-field">
      <Search size={16} aria-hidden="true" />
      <input
        {...props}
        id={id}
        type="search"
        aria-label={showLabel ? undefined : label}
        placeholder={props.placeholder ?? (showLabel ? "검색어 입력" : label)}
      />
    </div>
  );
  return showLabel ? (
    <div className="field field--search">
      <label htmlFor={id}>{label}</label>
      {control}
    </div>
  ) : (
    control
  );
}
export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: string;
}) {
  return <span className={`badge tone-${tone}`}>{children}</span>;
}
export function Avatar({
  name,
  src,
  size = "md",
  tone = "blue",
  decorative = false,
}: {
  name: string;
  src?: string;
  size?: "sm" | "md" | "lg";
  tone?: string;
  decorative?: boolean;
}) {
  // Plain image supports the same component in Next and isolated Storybook without a router adapter.
  return (
    <span
      className={`avatar avatar--${size} tone-${tone}`}
      title={decorative ? undefined : name}
      aria-hidden={decorative || undefined}
    >
      {src ? (
        // eslint-disable-next-line @next/next/no-img-element
        <img src={src} alt={decorative ? "" : name} width={48} height={48} />
      ) : (
        <span role="img" aria-label={name}>
          {name.slice(-2)}
        </span>
      )}
    </span>
  );
}
export function SegmentedControl<T extends string>({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: T;
  options: { value: T; label: string; icon?: ReactNode }[];
  onChange: (value: T) => void;
}) {
  return (
    <div className="segmented" role="group" aria-label={label}>
      {options.map((option) => (
        <button
          type="button"
          key={option.value}
          aria-pressed={option.value === value}
          onClick={() => onChange(option.value)}
        >
          {option.icon}
          {option.label}
        </button>
      ))}
    </div>
  );
}
export function Checkbox({
  label,
  checked,
  onChange,
  disabled,
}: {
  label: string;
  checked: boolean;
  onChange: (value: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="check-field">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(event) => onChange(event.target.checked)}
      />
      <span>{label}</span>
    </label>
  );
}
export function Dialog({
  open,
  onOpenChange,
  title,
  description = title,
  children,
  wide = false,
  size,
  footer,
  presentation = "dialog",
  contentPadding = "default",
  headerActions,
  onCloseAutoFocus,
}: {
  open: boolean;
  onOpenChange: (value: boolean) => void;
  title: string;
  description?: string;
  children: ReactNode;
  wide?: boolean;
  /** Detail surfaces may use the 960px preset; wide remains compatible. */
  size?: "default" | "wide" | "detail";
  footer?: ReactNode;
  presentation?: "dialog" | "sheet";
  /** Use none only when the content owns its body and footer spacing. */
  contentPadding?: "default" | "none";
  /** Compact, labelled actions beside the title; wraps beneath it on mobile. */
  headerActions?: ReactNode;
  onCloseAutoFocus?: (event: Event) => void;
}) {
  const trigger = useRef<HTMLElement | null>(null);
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="dialog-overlay" />
        <DialogPrimitive.Content
          className={`dialog ${size ? `dialog--${size}` : wide ? "dialog--wide" : ""} ${presentation === "sheet" ? "dialog--sheet" : ""}`}
          onOpenAutoFocus={() => {
            trigger.current = document.activeElement as HTMLElement;
          }}
          onCloseAutoFocus={(event) => {
            onCloseAutoFocus?.(event);
            if (event.defaultPrevented) return;
            event.preventDefault();
            if (trigger.current?.isConnected) trigger.current.focus();
          }}
        >
          <div
            className={`dialog-heading ${headerActions ? "dialog-heading--actions" : ""}`}
          >
            <DialogPrimitive.Title>{title}</DialogPrimitive.Title>
            {headerActions && (
              <div className="dialog-header-actions">{headerActions}</div>
            )}
            <DialogPrimitive.Close asChild>
              <button className="icon-button" aria-label="닫기">
                <X size={18} />
              </button>
            </DialogPrimitive.Close>
          </div>
          <DialogPrimitive.Description className="sr-only">
            {description}
          </DialogPrimitive.Description>
          {contentPadding === "none" ? (
            children
          ) : (
            <div className="dialog-body">{children}</div>
          )}
          {footer && <div className="dialog-action-region">{footer}</div>}
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}

type DialogAction = Omit<
  ButtonHTMLAttributes<HTMLButtonElement>,
  "children"
> & { label: ReactNode };
/** Secondary on the left, primary on the right, consistent across dialogs. */
export function DialogActions({
  primary,
  secondary,
  note,
}: {
  primary?: DialogAction & { tone?: "default" | "danger" };
  secondary?: DialogAction;
  note?: ReactNode;
}) {
  const {
    label: primaryLabel,
    tone = "default",
    ...primaryProps
  } = primary ?? { label: null };
  const { label: secondaryLabel, ...secondaryProps } = secondary ?? {
    label: null,
  };
  return (
    <div className="dialog-actions">
      {note && <div className="dialog-actions-note">{note}</div>}
      <div className="dialog-actions-buttons">
        {secondary && (
          <Button {...secondaryProps} variant="secondary">
            {secondaryLabel}
          </Button>
        )}
        {primary && (
          <Button
            {...primaryProps}
            variant={tone === "danger" ? "danger" : "primary"}
          >
            {primaryLabel}
          </Button>
        )}
      </div>
    </div>
  );
}

export function Alert({ children }: { children: ReactNode }) {
  return (
    <div role="alert" className="alert">
      <AlertCircle size={18} aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}
export function EmptyState({
  icon,
  title,
  action,
}: {
  icon: ReactNode;
  title: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <span className="empty-icon">{icon}</span>
      <h3>{title}</h3>
      {action}
    </div>
  );
}
export function Toast({
  message,
  onUndo,
  onClose,
}: {
  message: string;
  onUndo?: () => void;
  onClose: () => void;
}) {
  return (
    <div className="toast" role="status">
      <Check size={18} aria-hidden="true" />
      <span>{message}</span>
      {onUndo && <button onClick={onUndo}>실행 취소</button>}
      <IconButton label="알림 닫기" onClick={onClose}>
        <X size={16} />
      </IconButton>
    </div>
  );
}
export function Table({ children }: { children: ReactNode }) {
  return (
    <div className="table-scroll">
      <table className="table">{children}</table>
    </div>
  );
}
